/**
 * BOSS Sistem Logları sayfası — audit trail görüntüleme.
 *
 * Sadece BOSS erişebilir.
 */

import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  Shield, RefreshCw, User, Calendar, Filter, ChevronDown, ChevronUp,
  ArrowRight, AlertCircle,
} from 'lucide-react'
import { loglariGetir, type SistemLog, type LogTipi } from '@/lib/services/sistem-log-service'

const TIP_ETIKETLERI: Record<LogTipi, string> = {
  'personel.olustur': 'Personel Oluşturuldu',
  'personel.guncelle': 'Personel Güncellendi',
  'personel.sil': 'Personel Pasifleştirildi',
  'personel.geriYukle': 'Personel Geri Yüklendi',
  'maas.hareketEkle': 'Maaş Hareketi Eklendi',
  'maas.hareketSil': 'Maaş Hareketi Silindi',
  'maas.odemeEkle': 'Maaş Ödemesi Eklendi',
  'maas.odemeIptal': 'Maaş Ödemesi İptal',
  'izin.onayla': 'İzin Onaylandı',
  'izin.reddet': 'İzin Reddedildi',
  'izin.iptal': 'İzin İptal',
  'avans.onayla': 'Avans Onaylandı',
  'avans.reddet': 'Avans Reddedildi',
  'avans.odendi': 'Avans Ödendi',
  'avans.kararDegistir': 'Avans Kararı Değiştirildi',
  'puantaj.ekle': 'Puantaj Eklendi',
  'puantaj.sil': 'Puantaj Silindi',
  'ayKapanisi.kapat': 'Ay Kapatıldı',
  'ayKapanisi.ac': 'Ay Açıldı',
  'sube.ekle': 'Şube Eklendi',
  'sube.sil': 'Şube Silindi',
  'pozisyon.ekle': 'Pozisyon Eklendi',
  'pozisyon.sil': 'Pozisyon Silindi',
  'ayarlar.degistir': 'Ayar Değişti',
  'diger': 'Diğer',
}

const ALAN_ETIKETLERI: Record<string, string> = {
  isim: 'Ad Soyad',
  telefon: 'Telefon',
  email: 'E-posta',
  dogumTarihi: 'Doğum Tarihi',
  iseBaslamaTarihi: 'İşe Başlama Tarihi',
  adres: 'Adres',
  pozisyonIdleri: 'Pozisyonlar',
  subeIdleri: 'Şubeler',
  calismaTipi: 'Çalışma Tipi',
  takvimTipi: 'Çalışma Takvimi',
  maasTipi: 'Maaş Tipi',
  ucret: 'Ücret',
  fazlaMesaiKatsayisi: 'Fazla Mesai Katsayısı',
  aylikStandartSaat: 'Aylık Standart Saat',
  sabitEkOdeme: 'Sabit Ek Ödeme',
  sabitKesinti: 'Sabit Kesinti',
  ibanNo: 'IBAN',
  yillikIzinBaslangicBakiye: 'Yıllık İzin Bakiyesi',
  whatsappBildirimi: 'WhatsApp Bildirimi',
}

function rolEtiket(rol: string): string {
  switch (rol) {
    case 'boss': return 'BOSS'
    case 'sorumlu': return 'Sorumlu'
    case 'muhasebe': return 'Muhasebe'
    case 'personel': return 'Personel'
    case 'sistem': return 'Sistem'
    default: return rol
  }
}

function rolRengi(rol: string): string {
  switch (rol) {
    case 'boss': return 'bg-purple-100 text-purple-800 border-purple-200'
    case 'sorumlu': return 'bg-blue-100 text-blue-800 border-blue-200'
    case 'muhasebe': return 'bg-amber-100 text-amber-800 border-amber-200'
    case 'personel': return 'bg-gray-100 text-gray-700 border-gray-200'
    case 'sistem': return 'bg-green-100 text-green-700 border-green-200'
    default: return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

function degerGoster(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'Evet' : 'Hayır'
  if (Array.isArray(v)) return `[${v.length} öğe]`
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function LoglarPage() {
  const [loglar, setLoglar] = useState<SistemLog[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [filtreTip, setFiltreTip] = useState<'hepsi' | LogTipi>('hepsi')
  const [filtreRol, setFiltreRol] = useState<'hepsi' | string>('hepsi')
  const [genisletilen, setGenisletilen] = useState<Set<string>>(new Set())

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      const liste = await loglariGetir({ limit: 200 })
      setLoglar(liste)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Loglar yüklenemedi: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }, [])

  useEffect(() => { yukle() }, [yukle])

  const filtreli = useMemo(() => {
    return loglar.filter((l) => {
      if (filtreTip !== 'hepsi' && l.tip !== filtreTip) return false
      if (filtreRol !== 'hepsi' && l.yapanRol !== filtreRol) return false
      return true
    })
  }, [loglar, filtreTip, filtreRol])

  function genislet(id: string) {
    setGenisletilen((g) => {
      const n = new Set(g)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            Sistem Logları
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Tüm hassas değişiklikler burada loglanır. Son 200 kayıt gösterilir.
          </p>
        </div>
        <button
          onClick={yukle}
          disabled={yukleniyor}
          className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1 text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${yukleniyor ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      <div className="card">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filtreTip}
            onChange={(e) => setFiltreTip(e.target.value as 'hepsi' | LogTipi)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1"
          >
            <option value="hepsi">Tüm tipler</option>
            {(Object.keys(TIP_ETIKETLERI) as LogTipi[]).map((t) => (
              <option key={t} value={t}>{TIP_ETIKETLERI[t]}</option>
            ))}
          </select>
          <select
            value={filtreRol}
            onChange={(e) => setFiltreRol(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1"
          >
            <option value="hepsi">Tüm roller</option>
            <option value="boss">BOSS</option>
            <option value="muhasebe">Muhasebe</option>
            <option value="sorumlu">Sorumlu</option>
            <option value="personel">Personel</option>
          </select>
          <span className="text-sm text-gray-500 ml-auto">
            {filtreli.length} kayıt
          </span>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      {yukleniyor && loglar.length === 0 ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filtreli.length === 0 ? (
        <div className="card text-center py-12">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-700">Bu filtrede log kaydı yok.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtreli.map((log) => {
            const acik = genisletilen.has(log.id)
            const muhasebeMu = log.yapanRol === 'yonetici'
            const olumsuzTip = ['personel.sil', 'maas.hareketSil', 'maas.odemeIptal'].includes(log.tip)

            return (
              <div
                key={log.id}
                className={`card transition-colors ${
                  muhasebeMu && log.degisiklikler && log.degisiklikler.length > 0
                    ? 'border-amber-300 bg-amber-50/40'
                    : olumsuzTip
                    ? 'border-red-200 bg-red-50/40'
                    : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border ${rolRengi(log.yapanRol)}`}>
                        {rolEtiket(log.yapanRol)}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                        {TIP_ETIKETLERI[log.tip]}
                      </span>
                      {muhasebeMu && log.degisiklikler && log.degisiklikler.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded-full inline-flex items-center gap-0.5 font-bold">
                          <AlertCircle className="w-2.5 h-2.5" />
                          İnceleme Gerekli
                        </span>
                      )}
                    </div>

                    <p className="font-medium text-gray-900 text-sm">{log.baslik}</p>

                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {log.yapanIsim ?? log.yapanId.slice(0, 8)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {log.tarih.toDate().toLocaleString('tr-TR', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>

                    {acik && log.degisiklikler && log.degisiklikler.length > 0 && (
                      <div className="mt-2 space-y-1 bg-white border border-gray-200 rounded-lg p-2.5">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">
                          Değişiklikler
                        </p>
                        {log.degisiklikler.map((d, i) => (
                          <div key={i} className="text-xs">
                            <div className="font-semibold text-gray-700">
                              {ALAN_ETIKETLERI[d.alan] ?? d.alan}
                            </div>
                            <div className="flex items-center gap-1 text-gray-600 ml-2 flex-wrap">
                              <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                                {degerGoster(d.eski)}
                              </span>
                              <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded">
                                {degerGoster(d.yeni)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {acik && log.ekBilgi && Object.keys(log.ekBilgi).length > 0 && (
                      <div className="mt-2 bg-white border border-gray-200 rounded-lg p-2.5 text-xs">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">
                          Ek Bilgi
                        </p>
                        {Object.entries(log.ekBilgi).map(([k, v]) => (
                          <div key={k} className="text-gray-700">
                            <span className="font-semibold">{k}:</span> {degerGoster(v)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {((log.degisiklikler && log.degisiklikler.length > 0) || log.ekBilgi) && (
                    <button
                      onClick={() => genislet(log.id)}
                      className="text-gray-400 hover:text-gray-700 flex-shrink-0"
                      title={acik ? 'Daralt' : 'Genişlet'}
                    >
                      {acik ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
